import { useRef } from "react";
import type { AgGridReact } from "@ag-grid-community/react";
import type { FacetTableData } from "./facet-table-helpers";
import FacetResultTableBatchToolbar from "./FacetResultTableBatchToolbar";
import { useFacetResultTableBatchEdit } from "./useFacetResultTableBatchEdit";
import { initializeAgGrid } from "../../../../helpers/ag-grid-init";
import FacetResultTableGrid from "./FacetResultTableGrid";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

initializeAgGrid();

interface FacetResultTableProps {
    table: FacetTableData;
    onRowOrderChange?: (rowLabels: string[]) => void;
    onColumnOrderChange?: (columnHeaders: string[]) => void;
    sortOrderPersistDisabled?: boolean;
    allowColumnReorder?: boolean;
    onBatchSave?: (table: FacetTableData) => void | Promise<void>;
    isSaving?: boolean;
}

export default function FacetResultTable({
    table,
    onRowOrderChange,
    onColumnOrderChange,
    sortOrderPersistDisabled,
    allowColumnReorder,
    onBatchSave,
    isSaving = false,
}: FacetResultTableProps) {
    const gridRef = useRef<AgGridReact>(null);
    const { isEditing, handleBatchEditingStopped, handleStartEdit, handleSave, handleCancel } =
        useFacetResultTableBatchEdit({ table, onBatchSave, gridRef });

    return (
        <div className="flex w-full min-w-0 flex-col px-2 py-2 sm:p-4">
            {onBatchSave ? (
                <FacetResultTableBatchToolbar
                    isEditing={isEditing}
                    isSaving={isSaving}
                    onStartEdit={handleStartEdit}
                    onSave={handleSave}
                    onCancel={handleCancel}
                />
            ) : null}
            <div className="ag-theme-quartz w-full overflow-hidden">
                <FacetResultTableGrid
                    ref={gridRef}
                    table={table}
                    onRowOrderChange={onRowOrderChange}
                    onColumnOrderChange={onColumnOrderChange}
                    sortOrderPersistDisabled={sortOrderPersistDisabled}
                    allowColumnReorder={allowColumnReorder}
                    isEditing={isEditing}
                    onBatchEditingStopped={onBatchSave ? handleBatchEditingStopped : undefined}
                />
            </div>
        </div>
    );
}
